import { FC, useState } from 'react'

import styled from 'styled-components'
import { useParams } from 'react-router-dom'

import { FullScreenModal } from 'src/Components/FullScreenModal'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { useUpdateSpotifyCampaignTrackNoteMutation } from 'src/Redux/spotify-campaignApi'
import { PrimaryTextArea } from 'src/Components/PrimaryTextArea'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'

const RESTRICTED_CATEGORY_MAP: Record<string, string> = {
  email: 'email',
  phone: 'phone number',
  link: 'link',
  card: 'card number',
  iban: 'IBAN',
}

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  height: 100%;
  padding-bottom: 50px;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    padding: 0 24px 50px;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  padding-top: 23px;
  margin-bottom: 4px;
`

const SubTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-weight: 400;
  font-size: 14px;
  font-style: normal;
  line-height: 150%;
  margin-bottom: 24px;
`

const ErrorText = styled.div`
  color: ${({ theme }) => theme.color.mediumRed};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  margin-top: 8px;
`

const ButtonBar = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 24px;
  position: fixed;
  bottom: 0;
  background-color: ${({ theme }) => theme.color.white};
  padding: 8px 16px;
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;
`
const TEXTAREA_MAX_LENGTH = 300

export const TrackNoteModalWrapper: FC = () => {
  const { number: campaignId } = useParams<{ number: string }>()
  const { campaignDetails } = useSpotifyCampaignStatus()
  const { setSearch } = useCustomSearchParams()
  const [note, setNote] = useState(campaignDetails?.trackNote ?? '')
  const [error, setError] = useState('')
  const [updateTrackNote, { isLoading }] =
    useUpdateSpotifyCampaignTrackNoteMutation()

  const handleClose = (): void => {
    setSearch({ modal: null })
  }

  const handleSave = (): void => {
    if (!campaignId) {
      return
    }
    const trimmed = note.trim()
    updateTrackNote({ id: campaignId, trackNote: trimmed })
      .unwrap()
      .then(() => setSearch({ modal: null }))
      .catch((err) => {
        if (err?.data?.message === 'track-note-restricted-content') {
          const categories: string[] = err?.data?.categories ?? []
          const reasons = categories
            .map((c) => RESTRICTED_CATEGORY_MAP[c] ?? c)
            .join(', ')
          setError(`Remove ${reasons} to save your track note.`)
        } else if (trimmed) {
          setError('Failed to save your track note. Please try again.')
        } else {
          setSearch({ modal: null })
        }
      })
  }

  return (
    <FullScreenModal
      showCloseButton
      onCancel={handleClose}
      prefixCls='track-note'
      open
    >
      <ContentWrapper>
        <Title>Track note</Title>
        <SubTitle>
          Help others feel your music. Share the inspiration, a quick backstory,
          or the mood you were aiming for.
        </SubTitle>
        <PrimaryTextArea
          placeholder='Note (optional)'
          maxLength={TEXTAREA_MAX_LENGTH}
          count={{
            show: (count) => `${count.count}/${TEXTAREA_MAX_LENGTH}`,
          }}
          value={note ?? ''}
          onChange={(e) => {
            setNote(e.target.value)
            setError('')
          }}
          style={{ height: 180, resize: 'none' }}
        />
        {error && <ErrorText>{error}</ErrorText>}
      </ContentWrapper>
      <ButtonBar>
        <PrimaryDottedButton onClick={handleClose}>Go Back</PrimaryDottedButton>
        <StyledCTAButton onClick={handleSave} loading={isLoading}>
          Save info
        </StyledCTAButton>
      </ButtonBar>
    </FullScreenModal>
  )
}
